import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FiChevronLeft, FiEdit3, FiUploadCloud, FiInfo } from 'react-icons/fi';
import DashboardPageWrapper from '../../components/layout/DashboardPageWrapper';

function MethodCard({
  icon: Icon,
  iconBgClass,
  iconClass,
  title,
  description,
  badge,
  badgeClass,
  onClick,
  disabled = false,
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`flex flex-col items-center text-center w-full rounded-xl border border-gray-200 bg-white p-6 sm:p-8 transition-all
        ${disabled
          ? 'opacity-70 cursor-not-allowed'
          : 'hover:border-green-300 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-green-500/20'
        }`}
    >
      <div className={`w-14 h-14 rounded-full flex items-center justify-center mb-4 ${iconBgClass}`}>
        <Icon size={26} className={iconClass} />
      </div>
      <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
      <p className="text-sm text-gray-500 leading-relaxed flex-1">{description}</p>
      <span className={`mt-5 inline-block text-xs font-semibold px-3 py-1 rounded-full ${badgeClass}`}>
        {badge}
      </span>
    </button>
  );
}

export default function InvestorOnboardingMethodPage({ onSelectManual, onSelectAutomated }) {
  const navigate = useNavigate();

  const goBack = () => navigate('/investors/list');

  const goManual = () => {
    if (onSelectManual) {
      onSelectManual();
      return;
    }
    navigate('/investors/list');
  };

  const onCmrClick = () => {
    if (onSelectAutomated) {
      onSelectAutomated();
      return;
    }
    navigate('/investors/list');
  };

  return (
    <DashboardPageWrapper>
      <div className="max-w-4xl mx-auto w-full">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Add New Investor</h1>
            <p className="text-sm text-gray-500 mt-1.5">
              Choose how you would like to add the investor to your network.
            </p>
          </div>
          <button
            type="button"
            onClick={goBack}
            className="shrink-0 self-start flex items-center gap-1.5 px-4 py-2 rounded-lg border border-green-200 bg-green-50 text-green-700 text-sm font-semibold hover:bg-green-100 transition-colors"
          >
            <FiChevronLeft size={16} />
            Go Back
          </button>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 sm:p-10">
          <div className="text-center mb-8">
            <h2 className="text-xl font-bold text-gray-900">Select How to Add Investor</h2>
            <p className="text-sm text-gray-500 mt-1">Choose the method that works best for you</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6 max-w-3xl mx-auto">
            <MethodCard
              icon={FiEdit3}
              iconBgClass="bg-blue-50"
              iconClass="text-blue-600"
              title="Manually"
              description="Enter investor details manually by providing mobile number, PAN, bank details step by step"
              badge="Traditional Method"
              badgeClass="bg-blue-50 text-blue-700"
              onClick={goManual}
            />
            <MethodCard
              icon={FiUploadCloud}
              iconBgClass="bg-green-50"
              iconClass="text-green-600"
              title="CMR Upload"
              description="Upload a Client Master Report (CMR) PDF to automatically extract investor details"
              badge="Fast & Automated"
              badgeClass="bg-green-50 text-green-700"
              onClick={onCmrClick}
            />
          </div>

          <div className="mt-8 pt-6 border-t border-gray-100 flex items-start gap-2.5 max-w-3xl mx-auto">
            <FiInfo className="text-amber-500 shrink-0 mt-0.5" size={18} />
            <p className="text-sm text-gray-500">
              <span className="font-medium text-gray-600">Tip:</span>{' '}
              CMR upload can automatically extract mobile number, PAN, email, and other details from your document
            </p>
          </div>
        </div>
      </div>
    </DashboardPageWrapper>
  );
}
